// kitchen/shared/kds/diner-tag.ts
//
// Feature 268 — the diner tag, read at service time.
//
// The tag is `KitchenOrderItem.subAccount`: the short label attached to a line
// to say who it is for. A number when the waiter writes it in the cart, a first
// name when the table orders from the QR menu and picks a person. Until this
// feature it was read only at the till, to split the bill.
//
// Everything a surface needs to decide lives here, and nothing else does. The
// reason is not tidiness: neither frontend can test a component. Both jest
// configs (kitchen/web/jest.config.cjs, kitchen/expo-app/jest.config.js) run
// node-env ts-jest over pure TypeScript and exclude .tsx on purpose, because a
// renderer would be three new dependencies. A decision written inside a chip is
// therefore a decision nobody can check.
//
// ⚠️ MIRROR: kitchen/expo-app resolves "@shared/*" to its OWN tree, so this file
// has a hand-kept twin at kitchen/expo-app/shared/kds/diner-tag.ts. The two must
// stay identical; a test compares their answers. The neighbouring table-board
// library drifted for want of that test.

/** Anything with a sub-account on it — an order line, grouped or not. */
export interface DinerTagged {
  subAccount?: string | null;
}

/**
 * The colours a tag can take, in the order the hash walks them.
 *
 * Moved here verbatim from `labelColor` in the waiter app's SubcontoModal, where
 * they have been since sub-accounts existed. They are pinned by a test: a diner
 * who was blue in the cart must not be orange at the pass.
 */
const TAG_COLOURS = [
  '#4361EE', // blue
  '#2EC4B6', // teal
  '#FF6B35', // orange
  '#E67E22', // amber
  '#9b5de5', // purple
  '#e84393', // pink
  '#10b981', // green
  '#ef4444', // red
] as const;

/** One character, and the only mark a shortened tag carries. */
const ELLIPSIS = '…';

/**
 * The tag as it should be read: trimmed, or null when there is none.
 *
 * Null, undefined, "" and whitespace are one single thing — no tag — because a
 * waiter who opened the assignment sheet and closed it again has not named
 * anybody, and a blank chip is exactly the placeholder FR-003 forbids.
 *
 * "0" is a tag. It is falsy in JavaScript and it is a diner in a dining room.
 */
export function dinerTag(item: DinerTagged): string | null {
  const raw = item?.subAccount;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The whole decision, in one call: the tag to draw, or null to draw nothing.
 *
 * `enabled` is the venue's switch, passed in by whoever owns the client config
 * on that screen. The chip never fetches it: a comanda of twelve lines would
 * otherwise ask twelve times for one answer.
 */
export function shouldShowDinerTag(item: DinerTagged, enabled: boolean): string | null {
  if (!enabled) return null;
  return dinerTag(item);
}

/**
 * The colour for a tag. Same tag, same colour, on every surface, for ever.
 *
 * The hash is the one the app has always used, `& 0xffffffff` included — that
 * mask makes the result a signed 32-bit integer, which is why the absolute
 * value is taken before the modulo. Copying it faithfully is the point: this
 * function exists to keep two trees agreeing, not to be a better hash.
 */
export function dinerTagColor(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 31 + tag.charCodeAt(i)) & 0xffffffff;
  }
  return TAG_COLOURS[Math.abs(hash) % TAG_COLOURS.length];
}

/**
 * The tag, shortened to fit, with the start preserved.
 *
 * The start is what distinguishes one diner from another — "Mario" and "Marina"
 * part at the fourth letter, never at the last — and on 80 mm paper there is
 * room for very little. Counting is done over code points, so an emoji in a
 * name cannot be cut in half into a broken character.
 *
 * A bound of zero or less returns nothing at all, which is the honest answer
 * when there is no room: the caller draws no chip rather than a mark meaning
 * nothing.
 */
export function shortenDinerTag(tag: string, maxChars: number): string {
  if (maxChars <= 0) return '';
  const chars = Array.from(tag);
  if (chars.length <= maxChars) return tag;
  if (maxChars === 1) return ELLIPSIS;
  return chars.slice(0, maxChars - 1).join('') + ELLIPSIS;
}
